// src/services/productionNutrition.ts
//
// Phase 2D nutrition + FDA Nutrition Facts label generator. Ported nearly
// verbatim from the GeniBatch standalone app, trimmed to a single format
// (FDA standard vertical) — the most common food-service requirement.
// Canada bilingual + FDA Linear can be added in a follow-up.
//
// Disclaimer: nutrition labels are regulated artifacts. Rounding rules below
// follow FDA 21 CFR 101.9 and the FDA 2016 Daily Value table. Validate
// generated labels against the current FDA spec before commercial printing.

import { jsPDF } from 'jspdf';
import type {
  ProductionItem, ProductionRecipe, ItemNutrition,
} from '../types/production';

// ─── Unit conversions to grams (for nutrition scaling) ────────────────────

const UNIT_TO_GRAMS: Record<string, number> = {
  g: 1,
  kg: 1000,
  mg: 0.001,
  oz: 28.3495,
  lb: 453.592,
  lbs: 453.592,
  ml: 1,          // approx water density
  l: 1000,
  cup: 236.588,
  tbsp: 14.787,
  tsp: 4.929,
  'fl oz': 29.5735,
  floz: 29.5735,
  ea: 100,        // fallback for piece-counted ingredients
  each: 100,
};

export function unitToGrams(qty: number, unit: string): number {
  const u = (unit || 'g').toLowerCase().trim();
  const factor = UNIT_TO_GRAMS[u] ?? 1;
  return qty * factor;
}

/** Pull a gram count out of a serving-size string like "227g" or "1 cup (240g)". */
export function parseServingGrams(s: string | undefined): number | null {
  if (!s) return null;
  const direct = s.match(/\(?\s*(\d+(?:\.\d+)?)\s*g\s*\)?/i);
  if (direct) return parseFloat(direct[1]);
  const unitMatch = s.match(/(\d+(?:\.\d+)?)\s*(oz|tbsp|tsp|cup|ml|fl ?oz|lb|kg)\b/i);
  if (unitMatch) {
    const q = parseFloat(unitMatch[1]);
    return unitToGrams(q, unitMatch[2]);
  }
  return null;
}

// ─── Aggregate recipe nutrition ───────────────────────────────────────────

const NUT_KEYS: Array<keyof ItemNutrition> = [
  'kcal', 'fat', 'satfat', 'transfat', 'cholesterol', 'sodium', 'carbs',
  'fiber', 'sugar', 'addedSugar', 'protein', 'vitD', 'calcium', 'iron', 'potassium',
];

export interface AggregatedNutrition extends ItemNutrition {
  batchGrams: number;
}

/**
 * Sum nutrition across all recipe components (item.nutrition is per-100g),
 * returning per-batch totals. Components without nutrition data contribute 0.
 */
export function aggregateRecipeNutrition(
  recipe: ProductionRecipe,
  itemsById: Record<string, ProductionItem>,
): AggregatedNutrition {
  const totals: AggregatedNutrition = { batchGrams: 0 };
  for (const c of recipe.components) {
    const item = itemsById[c.itemId];
    if (!item) continue;
    const grams = unitToGrams(c.qty || 0, c.unit || item.unit);
    totals.batchGrams += grams;
    if (!item.nutrition) continue;
    // nutrition is per-100g; component contribution = (grams/100) × per100
    const scale = grams / 100;
    for (const k of NUT_KEYS) {
      const v = item.nutrition[k];
      if (typeof v === 'number') {
        totals[k] = (totals[k] ?? 0) + v * scale;
      }
    }
  }
  return totals;
}

/** Per-serving nutrition derived from per-batch totals, scaled by serving fraction. */
export function perServing(
  batch: AggregatedNutrition,
  servingGrams: number,
): ItemNutrition {
  const out: ItemNutrition = {};
  if (batch.batchGrams <= 0 || servingGrams <= 0) return out;
  const factor = servingGrams / batch.batchGrams;
  for (const k of NUT_KEYS) {
    const v = batch[k];
    if (typeof v === 'number') out[k] = v * factor;
  }
  return out;
}

/** Aggregate allergens across all recipe components, deduped. */
export function aggregateAllergens(
  recipe: ProductionRecipe,
  itemsById: Record<string, ProductionItem>,
): string[] {
  const set = new Set<string>();
  for (const c of recipe.components) {
    const item = itemsById[c.itemId];
    if (!item?.allergens) continue;
    for (const a of item.allergens) set.add(a);
  }
  return Array.from(set).sort();
}

// ─── FDA rounding rules (21 CFR 101.9) ────────────────────────────────────

const US_DV: Record<string, number> = {
  fat: 78, satfat: 20, cholesterol: 300, sodium: 2300, carbs: 275,
  fiber: 28, addedSugar: 50, protein: 50, vitD: 20, calcium: 1300,
  iron: 18, potassium: 4700,
};

const R = {
  calories(v: number): string {
    if (v < 5) return '0';
    if (v <= 50) return String(Math.round(v / 5) * 5);
    return String(Math.round(v / 10) * 10);
  },
  fat(v: number): string {
    if (v < 0.5) return '0';
    if (v < 5) return (Math.round(v * 2) / 2).toFixed(1).replace(/\.0$/, '');
    return String(Math.round(v));
  },
  cholesterol(v: number): string {
    if (v < 2) return '0';
    if (v <= 5) return '5';
    return String(Math.round(v / 5) * 5);
  },
  sodium(v: number): string {
    if (v < 5) return '0';
    if (v <= 140) return String(Math.round(v / 5) * 5);
    return String(Math.round(v / 10) * 10);
  },
  carb(v: number): string {
    if (v < 0.5) return '0';
    if (v < 1) return '<1';
    return String(Math.round(v));
  },
  protein(v: number): string {
    if (v < 0.5) return '0';
    if (v < 1) return '<1';
    return String(Math.round(v));
  },
  vitD(v: number): string {
    if (v < 0.5) return '0';
    if (v < 10) return (Math.round(v * 10) / 10).toFixed(1).replace(/\.0$/, '');
    return String(Math.round(v));
  },
  mineral(v: number): string {
    if (v < 0.5) return '0';
    if (v < 10) return (Math.round(v * 10) / 10).toFixed(1).replace(/\.0$/, '');
    return String(Math.round(v));
  },
  dv(val: number | undefined, daily: number | undefined): number {
    if (!daily || !val) return 0;
    return Math.round((val / daily) * 100);
  },
};

// ─── Label data + PDF generation ──────────────────────────────────────────

export interface LabelInput {
  recipeName: string;
  servingSize: string;       // e.g. "227g (1 cup)"
  servingsPerContainer: string | number;
  perServingNutrition: ItemNutrition;
  ingredientList: string[];   // for the INGREDIENTS line below the panel
  allergens: string[];
}

const PAD = 8;
const LABEL_WIDTH = 220;  // pt — natural FDA panel width
const PAGE_MARGIN = 54;   // 0.75" — Letter page outer margin
const ACCENT_RGB: [number, number, number] = [249, 110, 77];

/**
 * Draws ONLY the FDA Nutrition Facts panel content starting at (x0, y0).
 * Returns the y coordinate just below the panel content (caller draws the
 * border using this). The border is NOT drawn here — that's the wrapper's
 * job so it can size the rect to match the actual content.
 */
function drawFdaVerticalPanel(
  doc: jsPDF, n: ItemNutrition, servingSize: string, servingsPerContainer: string | number,
  x0: number, y0: number,
): number {
  const x = x0 + PAD;
  const cw = LABEL_WIDTH - PAD * 2;
  const right = x + cw;
  let y = y0 + PAD;

  // Title
  txt(doc, 'Nutrition Facts', x, y + 17, { size: 19, bold: true });
  y += 22;
  rule(doc, x, y, cw, 0.5); y += 2;

  // Servings
  txt(doc, `${servingsPerContainer || '—'} servings per container`, x, y + 8, { size: 8 });
  y += 11;
  txt(doc, 'Serving size', x, y + 9, { size: 10, bold: true });
  txt(doc, servingSize || '—', right, y + 9, { size: 10, bold: true, align: 'right' });
  y += 12;
  bar(doc, x, y, cw, 7); y += 7 + 2;

  // Calories
  txt(doc, 'Amount per serving', x, y + 6, { size: 7, bold: true });
  y += 9;
  txt(doc, 'Calories', x, y + 16, { size: 15, bold: true });
  txt(doc, R.calories(n.kcal || 0), right, y + 18, { size: 26, bold: true, align: 'right' });
  y += 22;
  bar(doc, x, y, cw, 4); y += 4 + 2;

  // % DV header
  txt(doc, '% Daily Value *', right, y + 7, { size: 7.5, bold: true, align: 'right' });
  y += 9;
  rule(doc, x, y, cw, 0.5);

  const rowH = 11.5;
  const row = (
    label: string, amount: string, dvVal: number | undefined, dvKey: string | null,
    opts: { bold?: boolean; indent?: number } = {},
  ) => {
    const ly = y + 8;
    const indent = opts.indent || 0;
    const dv = dvKey ? R.dv(dvVal, US_DV[dvKey]) : null;
    txt(doc, label, x + indent, ly, { size: 8.5, bold: !!opts.bold });
    const lw = doc.getTextWidth(label) + 3;
    txt(doc, amount, x + indent + lw, ly, { size: 8.5 });
    if (dv != null) txt(doc, `${dv}%`, right, ly, { size: 8.5, bold: true, align: 'right' });
    y += rowH;
    rule(doc, x, y, cw, 0.5);
  };

  row('Total Fat', `${R.fat(n.fat || 0)}g`, n.fat, 'fat', { bold: true });
  row('Saturated Fat', `${R.fat(n.satfat || 0)}g`, n.satfat, 'satfat', { indent: 10 });
  {
    const ly = y + 8;
    txt(doc, 'Trans', x + 10, ly, { size: 8.5, italic: true });
    const iw = doc.getTextWidth('Trans') + 3;
    txt(doc, `Fat ${R.fat(n.transfat || 0)}g`, x + 10 + iw, ly, { size: 8.5 });
    y += rowH; rule(doc, x, y, cw, 0.5);
  }
  row('Cholesterol', `${R.cholesterol(n.cholesterol || 0)}mg`, n.cholesterol, 'cholesterol', { bold: true });
  row('Sodium', `${R.sodium(n.sodium || 0)}mg`, n.sodium, 'sodium', { bold: true });
  row('Total Carbohydrate', `${R.carb(n.carbs || 0)}g`, n.carbs, 'carbs', { bold: true });
  row('Dietary Fiber', `${R.carb(n.fiber || 0)}g`, n.fiber, 'fiber', { indent: 10 });
  row('Total Sugars', `${R.carb(n.sugar || 0)}g`, undefined, null, { indent: 10 });
  row(`Includes ${R.carb(n.addedSugar || 0)}g Added Sugars`, '', n.addedSugar, 'addedSugar', { indent: 20 });
  row('Protein', `${R.protein(n.protein || 0)}g`, undefined, null, { bold: true });

  bar(doc, x, y, cw, 6); y += 6 + 1;

  const micro = (label: string, amount: string, dvVal: number | undefined, dvKey: string) => {
    const ly = y + 8;
    const dv = R.dv(dvVal, US_DV[dvKey]);
    txt(doc, `${label} ${amount}`, x, ly, { size: 8 });
    txt(doc, `${dv}%`, right, ly, { size: 8, align: 'right' });
    y += 10.5; rule(doc, x, y, cw, 0.5);
  };
  micro('Vitamin D', `${R.vitD(n.vitD || 0)}mcg`, n.vitD, 'vitD');
  micro('Calcium', `${R.mineral(n.calcium || 0)}mg`, n.calcium, 'calcium');
  micro('Iron', `${R.mineral(n.iron || 0)}mg`, n.iron, 'iron');
  micro('Potassium', `${R.mineral(n.potassium || 0)}mg`, n.potassium, 'potassium');

  // Footnote
  bar(doc, x, y, cw, 3); y += 3 + 3;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6.2);
  doc.setTextColor(0, 0, 0);
  const fn = '* The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.';
  const fnLines = doc.splitTextToSize(fn, cw);
  doc.text(fnLines, x, y + 5);
  y += fnLines.length * 7;

  return y + PAD;
}

/** Draws the item-info header at the top of the page (name + date + accent rule). */
function drawLabelHeader(doc: jsPDF, recipeName: string, x: number, top: number, pageW: number): number {
  let y = top;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(17); doc.setTextColor(20, 22, 30);
  const nameLines = doc.splitTextToSize(recipeName || 'Untitled', pageW - x * 2 - 120);
  doc.text(nameLines, x, y + 13);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(120, 120, 130);
  doc.text(`GeniReports  ·  ${new Date().toLocaleDateString()}`, pageW - x, top + 4, { align: 'right' });
  y += nameLines.length * 18 + 4;
  doc.setDrawColor(ACCENT_RGB[0], ACCENT_RGB[1], ACCENT_RGB[2]);
  doc.setLineWidth(1.5);
  doc.line(x, y + 4, pageW - x, y + 4);
  return y + 16;
}

/** Draws the ingredient + allergen block below the bordered nutrition panel. */
function drawExtras(
  doc: jsPDF, ingredientList: string[], allergens: string[],
  x0: number, y: number, blockWidth: number,
): number {
  const x = x0 + PAD;
  const cw = blockWidth - PAD * 2;
  if (ingredientList.length === 0 && allergens.length === 0) return y;
  y += 4;
  if (ingredientList.length > 0) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(0, 0, 0);
    const head = 'Ingredients: ';
    const body = head + ingredientList.join(', ');
    const lines = doc.splitTextToSize(body, cw);
    doc.text(lines, x, y + 6);
    y += lines.length * 8.5 + 3;
  }
  if (allergens.length > 0) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.8); doc.setTextColor(0, 0, 0);
    const lines = doc.splitTextToSize('Contains: ' + allergens.join(', '), cw);
    doc.text(lines, x, y + 6);
    y += lines.length * 9;
  }
  return y;
}

export function generateFdaVerticalLabelPdf(input: LabelInput): Blob {
  // US Letter page in portrait — matches GeniBatch
  const doc = new jsPDF({ unit: 'pt', orientation: 'portrait', format: 'letter', compress: true });
  const pageW = doc.internal.pageSize.getWidth();

  // 1. Header (recipe name + date + accent rule)
  const panelTop = drawLabelHeader(doc, input.recipeName, PAGE_MARGIN, PAGE_MARGIN, pageW);

  // 2. Nutrition panel at natural width (220pt), positioned at left margin
  const panelBottom = drawFdaVerticalPanel(
    doc, input.perServingNutrition, input.servingSize, input.servingsPerContainer,
    PAGE_MARGIN, panelTop,
  );

  // 3. Border around the panel (drawn after the content so we know exact height)
  doc.setDrawColor(0); doc.setLineWidth(1);
  doc.rect(PAGE_MARGIN, panelTop, LABEL_WIDTH, panelBottom - panelTop);

  // 4. Ingredients + allergens block below the panel (full panel width)
  drawExtras(doc, input.ingredientList, input.allergens, PAGE_MARGIN, panelBottom, LABEL_WIDTH);

  return doc.output('blob');
}

// ─── Low-level draw helpers ───────────────────────────────────────────────

function rule(doc: jsPDF, x: number, y: number, w: number, weight: number) {
  doc.setDrawColor(0);
  doc.setLineWidth(weight);
  doc.line(x, y, x + w, y);
}

function bar(doc: jsPDF, x: number, y: number, w: number, h: number) {
  doc.setFillColor(0, 0, 0);
  doc.rect(x, y, w, h, 'F');
}

interface TxtOpts {
  size?: number;
  bold?: boolean;
  italic?: boolean;
  align?: 'left' | 'right' | 'center';
}

function txt(doc: jsPDF, s: string, x: number, y: number, opts: TxtOpts = {}) {
  const { size = 8, bold = false, italic = false, align = 'left' } = opts;
  let style = 'normal';
  if (bold && italic) style = 'bolditalic';
  else if (bold) style = 'bold';
  else if (italic) style = 'italic';
  doc.setFont('helvetica', style);
  doc.setFontSize(size);
  doc.setTextColor(0, 0, 0);
  doc.text(s, x, y, { align });
}

// ─── High-level recipe → PDF ─────────────────────────────────────────────

export interface LabelOptions {
  servingSize: string;            // "227g"
  servingsPerContainer: number;   // 8
}

export function generateRecipeLabelPdf(
  recipe: ProductionRecipe,
  itemsById: Record<string, ProductionItem>,
  options: LabelOptions,
): Blob {
  const batch = aggregateRecipeNutrition(recipe, itemsById);
  const servingGrams = parseServingGrams(options.servingSize) || (batch.batchGrams / options.servingsPerContainer);
  const perServ = perServing(batch, servingGrams);
  const allergens = aggregateAllergens(recipe, itemsById);
  const ingredients = recipe.components
    .map((c) => itemsById[c.itemId]?.name)
    .filter((n): n is string => !!n);

  return generateFdaVerticalLabelPdf({
    recipeName: recipe.name,
    servingSize: options.servingSize,
    servingsPerContainer: options.servingsPerContainer,
    perServingNutrition: perServ,
    ingredientList: ingredients,
    allergens,
  });
}

export const ALLERGEN_LIST = [
  'Milk', 'Eggs', 'Fish', 'Shellfish', 'Tree Nuts', 'Peanuts',
  'Wheat', 'Soy', 'Sesame',
];
